import {put, call, all} from 'redux-saga/effects';
import Identification from "../../entities/identification";
import * as Actions from "../actions/identificationActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type IdentificationAction = {
    identificationId: string;
    identification: Identification;
    identifications: Identification[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneIdentification(action: IdentificationAction) {
	const session = tabSession.captureGeneration();
	try {
		const identification: Identification = yield call(api.findOne, 'identification', action.identificationId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_FETCH_ONE_SUCCESS, identificationId: action.identificationId, identification, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IDENTIFICATION_FETCH_ONE_FAILURE, identificationId: action.identificationId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {IdentificationAction} action
 */
export function* saveIdentification(action: IdentificationAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ IDENTIFICATION_SAVE saga', action.identification?.id);

		if (action.identification === undefined || action.identification === null) {
			console.error('saveIdentification received a null');
			throw new Error('saveIdentification received a null');
		}

		if (!(action.identification instanceof Identification)) {
			action.identification = new Identification(action.identification);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.identification.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.identification);
			const addResult = yield call(idb.add, 'identification', action.identification);
			if (addResult.isErr()) {
				console.error(`⮂ saveIdentification: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertIdentificationSave(action.identification, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.IDENTIFICATION_SAVE_FAILURE, identification: action.identification, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.IDENTIFICATION_SAVE, identification: action.identification, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertIdentificationSave(action.identification, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.IDENTIFICATION_SAVE_FAILURE, identification: action.identification, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {IdentificationAction} action
 * @param {Identification[]} action.identifications
 */
export function* saveManyIdentifications(action: IdentificationAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ IDENTIFICATION_SAVE_MANY saga', action.identifications.map(identification => identification.id).join(','));

        if (!Array.isArray(action.identifications)) {
            console.error('saveManyIdentifications expected an array, received', action.identifications);
            throw new Error('saveManyIdentifications expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveIdentification), under these queue keys
		const dispatchedAddedOns: number[] = action.identifications.map(identification => identification?.addedOn);
		const revertAll = () => action.identifications.map((identification, i) =>
			put(Actions.revertIdentificationSave(identification, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Identification[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.identifications.length; i++) {
				const identification = action.identifications[i];
				identification.persisted = false;
				ensureOperationId(identification);
				toSave.push(identification);
			}

			const addResult = yield call(idb.add, 'identification', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyIdentifications: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.IDENTIFICATION_SAVE_MANY_FAILURE, identifications: action.identifications, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.IDENTIFICATION_SAVE_MANY, identifications: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.IDENTIFICATION_SAVE_MANY_FAILURE, identifications: action.identifications, status: 500, err});

			console.error(`Error saving ${action.identifications.length} identifications to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {IdentificationAction} action
 */
export function* removeIdentification(action: IdentificationAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.identification === undefined || action.identification === null || !(action.identification instanceof Identification)) {
			console.error('removeIdentification expected type Identification, received', action.identification);
			reportQueued(false);
			throw new Error('removeIdentification expected type Identification');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.identification.status = 'D';
		action.identification.addedOn = newHRTimestamp();
		action.identification.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.identification);
		const addResult = yield call(idb.add, 'identification', action.identification);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeIdentification: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.IDENTIFICATION_REMOVE_FAILURE, identification: action.identification, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_REMOVE, identification: action.identification});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IDENTIFICATION_REMOVE_FAILURE, identification: action.identification, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Identification.
	*
	* @param {IdentificationAction} action
	*/
export function* restoreIdentification(action: IdentificationAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreIdentification expected getCurrentUserId() to return a value');
        return;
    }

	if (action.identification === undefined || action.identification === null) {
        console.error('restoreIdentification expected a Identification, received null');
        throw new Error('restoreIdentification expected a Identification, received null');
    }
    else if (!(action.identification instanceof Identification)) action.identification = new Identification(action.identification); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.identification.status = 'U';
		action.identification.addedOn = newHRTimestamp();
		action.identification.addedBy = userId;
		action.identification.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.identification);
		const addResult = yield call(idb.add, 'identification', action.identification);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreIdentification: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IDENTIFICATION_RESTORE_FAILURE, identification: action.identification, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IDENTIFICATION_RESTORE, identification: action.identification});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IDENTIFICATION_RESTORE_FAILURE, identification: action.identification, status: 500, err});

		console.error('Error restoring from database', err);
	}
}